import React, { Fragment, useState, useEffect, useContext } from 'react'
import AppContext from '../../contexts/AppContext'
import Breadcrumbs from '../Breadcrumbs'
import Header from '../Header'
import Footer from '../Footer'
import { breadcrumbsBy } from '../../services/locations'
import { numberFormat } from '../../services/posts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import useToastContext from "../../hooks/useToastContext";
import CurrencyInput from 'react-currency-input-field';

import { Helmet } from "react-helmet-async";

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.MIX_STRIPE_PUBLIC_KEY);

const CheckoutForm = ({ amount, clientSecret, onBack }) => {

    const stripe = useStripe()
    const elements = useElements()

    const appContext = useContext(AppContext)
    const { bannerServices } = appContext

    const [ message, setMessage ] = useState(null)
    const [ isLoading, setIsLoading ] = useState(false)

    const [ agreeToTerms, setAgreeToTerms ] = useState(false)

    const addToast = useToastContext()

    useEffect(() => {
        if (!stripe){
            return;
        }
        if (!clientSecret){
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            switch (paymentIntent.status) {
                case "succeeded":
                    setMessage("Payment succeeded!");
                    break;
                case "processing":
                    setMessage("Your payment is processing.");
                    break;
                case "requires_payment_method":
                    setMessage("Your payment was not successful, please try again.");
                    break;
                default:
                    setMessage("Something went wrong.");
                    break;
            }
        });

    }, [ stripe ]);

    const handleSubmit = e => {
        e.preventDefault()

        if (!stripe || !elements) return null

        if(!agreeToTerms){
            setMessage("You have to agree to the AdSitePro Privacy Policy, Terms of Use and Posting Rules to proceed.")
            return null
        }

        setIsLoading(true)

        stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${process.env.MIX_APP_URL}/account/credits-and-points`
            },
        }).then(({ error }) => {
            if (error.type === "card_error" || error.type === "validation_error") {
                setMessage(error.message)
            } else {
                setMessage("An unexpected error occured.")
            }
            setIsLoading(false)
        })

    }

    return (
        <form id="payment-form" onSubmit={handleSubmit}>

            <h5 className="mb-4">Add: <b>${numberFormat(amount)}</b></h5>

            {message &&
                <div id="payment-message" className="alert alert-danger">{message}</div>
            }

            <div className="mb-3">
                <PaymentElement
                    id="payment-element"
                    onFocus={() => setMessage(null)}
                />
            </div>

            {!isLoading &&
                <div className="form-check d-inline-block mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="agreeToTermsCheckbox"
                        checked={agreeToTerms}
                        onChange={e => setAgreeToTerms(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="agreeToTermsCheckbox">
                        By checking this I agree to AdSitePro <a href="/article/privacy">Privacy Policy</a>, <a href="/article/terms">Terms of Use</a> and <a href="/article/posting-rules">Posting Rules</a>.
                    </label>
                </div>
            }

            <div className="mt-2 text-center">
                {!isLoading &&
                    <button
                        className="btn btn-outline-secondary my-2 me-2"
                        onClick={onBack}
                    >{'< '}Back</button>
                }
                <button
                    id="submit"
                    disabled={isLoading || !stripe || !elements}
                    className="btn btn-success my-2"
                >
                    <span id="button-text">
                        {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Pay now"}
                    </span>
                </button>
            </div>

        </form>
    );
};

const PaymentOptions = ({ onSuccess, onError }) => {

    const appContext = useContext(AppContext)
    const { pointServices, bannerServices, transctionServices } = appContext

    const [ clientSecret, setClientSecret ] = useState()
    const [ purchasing, setPurchasing ] = useState(false)

    const [ type, setType ] = useState(false)
    const [ errors, setErrors ] = useState({})

    const [ amount, setAmount ] = useState(25)

    const addToast = useToastContext()

    useEffect(() => {
        if(['stripe'].includes(type)){
            setErrors({})
            transctionServices.intent( { amount }).then(res => {
                setClientSecret(res.clientSecret)
            }).catch(err => {
                const message = err?.data?.message ? err.data.message : 'Something went wrong'
                addToast({ type: 'danger', title: 'Error', message: message })
                if(err?.data?.errors) setErrors(err.data.errors)
                setType(false)
            })
        }
    }, [ type ])

    const appearance = {
        theme: 'stripe',
    }

    const options = {
        appearance,
        clientSecret,
    }

    return (
        <>
            {!['stripe'].includes(type) &&
                <form>
                    <div className="">
                        <h5 className="mb-3">Preload your account in cash</h5>
                        <div className="mb-3">
                            <CurrencyInput
                                id="add-funds-input"
                                className={'form-control' + (errors?.amount?.length > 0 ? ' is-invalid' : '')}
                                value={amount}
                                decimalsLimit={2}
                                decimalScale={2}
                                prefix="$"
                                onValueChange={value => setAmount(value)}
                            />
                            {errors?.amount?.length > 0 &&
                                <div className="text-center invalid-feedback">
                                    {errors.amount.join(', ')}
                                </div>
                            }
                        </div>
                        <div className="text-center my-3">
                            <button
                                className="btn btn-success btn-lg w-100"
                                type="button"
                                onClick={() => setType('stripe')}
                            >Add</button>
                        </div>
                    </div>
                </form>
            }

            {['stripe'].includes(type) &&
                <>
                    {clientSecret &&
                        <Elements stripe={stripePromise} options={options}>
                            <CheckoutForm
                                type={type}
                                amount={amount}
                                onBack={() => setType(null)}
                            />
                        </Elements>
                    }
                </>
            }
        </>
    )
}

const PointsSummary = ({ }) => {

    const { data, pointServices } = useContext(AppContext)
    const { app, isReady } = data

    const [ loading, setLoading ] = useState(false)
    const [ summary, setSummary ] = useState(null)

    const addToast = useToastContext()

    const loadSummary = () => {
        setLoading(true)
        pointServices.summary().then(res => {
            setSummary(res.summary)
            setLoading(false)
        }).catch(res => {
            setLoading(false)
            const message = res?.data?.message ? res.data.message : 'Something went wrong'
            addToast({message: message, type: 'danger'})
        })
    }

    useEffect(() => {
        if(isReady) loadSummary()
    }, [ isReady ])

    return (
        <div className="">
            {summary &&
                <h4 className="card-title mb-3">Available Funds: <b>${numberFormat(summary.points / 100)}</b></h4>
            }
            {loading && <FontAwesomeIcon icon={faSpinner} spin />}
        </div>
    )

}

const AddFunds = ({ match, history }) => {

    const { data } = useContext(AppContext)
    const { app } = data
    const { location, user } = app

    const title = "Add Funds"

    return (
        <Fragment>
            <div>
                <Header history={history} />
                <main className="pb-4">


                        <Helmet><title>{title + ' | ' + process.env.MIX_APP_NAME}</title></Helmet>

                        <div className="py-5 bg-white mb-5">
                            <div className="container">
                                <div className="col-lg-8 mx-auto" x-data="{ open: false }">
                                    <h1 className="h2 my-0 font-weight-bold">
                                        <span className="py-1">{title}</span>
                                    </h1>
                                    <div className="mt-3">
                                        <Breadcrumbs list={breadcrumbsBy(location, [
                                            {url: '/account', title: 'Dashboard'},
                                            {url: '/account/credits-and-points', title: 'Finance'},
                                            {title}
                                        ])} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6 mx-auto">

                                <div className="card">
                                    <div className="card-body">
                                        <PointsSummary />
                                        <PaymentOptions />
                                    </div>
                                </div>

                            </div>
                        </div>

                </main>
            </div>
            <Footer />
        </Fragment>
    )
}

export default AddFunds
